
import React, { memo, useEffect, useState } from 'react';
import { PreviewHeader } from './preview/PreviewHeader';
import { PreviewLoader } from './preview/PreviewLoader';
import { PreviewIframe } from './preview/PreviewIframe';
import { usePreviewProcessor } from './preview/usePreviewProcessor';

interface PreviewPaneProps {
  previewHtml: string;
  getPreviewHtmlWithExampleValues: (html: string) => string;
  isChangeDetected?: boolean;
}

export const PreviewPane = memo(function PreviewPane({ 
  previewHtml, 
  getPreviewHtmlWithExampleValues,
  isChangeDetected = false
}: PreviewPaneProps) {
  const [previewError, setPreviewError] = useState<string | null>(null);
  
  const { renderedHtml, isLoading, handleIframeLoad } = usePreviewProcessor({
    previewHtml,
    getPreviewHtmlWithExampleValues,
    isChangeDetected
  });
  
  // Handle errors in the preview rendering
  const handlePreviewError = (error: Error) => {
    console.error('Preview rendering error:', error);
    setPreviewError('Preview rendering failed: ' + error.message);
  };
  
  // Reset error when new HTML content is available
  useEffect(() => {
    if (previewHtml) {
      setPreviewError(null);
    }
  }, [previewHtml]);
  
  // Debug logging for preview updates
  useEffect(() => {
    if (previewHtml) {
      console.log('PreviewPane received new HTML content, length:', previewHtml.length);
    }
  }, [previewHtml]);
  
  return (
    <div className="editor-container h-full flex flex-col bg-white border-l">
      <PreviewHeader isLoading={isLoading} previewError={previewError} />
      
      <div 
        className="flex-1 relative w-full overflow-hidden" 
        style={{ 
          height: '550px',
          minHeight: '550px',
          backgroundColor: '#f9f9f9',
          position: 'relative'
        }}
      >
        {/* Empty placeholder to maintain dimensions during loading */}
        {isLoading && <PreviewLoader />}
        
        {renderedHtml ? (
          <PreviewIframe 
            renderedHtml={renderedHtml} 
            isLoading={isLoading}
            onLoad={handleIframeLoad}
            onError={handlePreviewError}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center p-4 text-gray-500">
            {previewError ? 
              <div className="text-red-500 text-center p-4">
                <p className="font-bold">Preview Error</p>
                <p>{previewError}</p>
              </div> :
              'Waiting for content to preview...'
            }
          </div>
        )}
      </div>
    </div>
  );
});

PreviewPane.displayName = 'PreviewPane';
